import { match, P } from 'ts-pattern';
import { GraphNavigationState } from './use-graph-navigation';
import { GraphId, GraphRefType } from '../types';
import { useStore } from '@/store';
import { EntryType } from '@morph-mapper/types';
import { useCallback } from 'react';

export const useGraphResolver = (entryId: string, graphId?: GraphId) => {
  const [initGraph, linkGraph, getGraphRef, INode, IEntry] = useStore(
    ({ graphs: g, entries: e }) => [
      g.init,
      e.linkGraph,
      g.getRef,
      g.node,
      e.entry,
    ]
  );

  const resolveParameterGraph = useCallback(
    ({
      parentGraphId,
      ownerNodeId,
      paramKey,
    }: GraphNavigationState): GraphId => {
      let resolvedId = graphId;
      if (!resolvedId) {
        resolvedId = initGraph({
          ref: {
            kind: GraphRefType.Param,
            graphId: parentGraphId,
            fieldId: entryId,
            ownerNodeId,
            paramKey,
          },
        });

        INode(parentGraphId, ownerNodeId).setOptionAsGraph(
          paramKey,
          resolvedId
        );
      }

      // Sanity check
      if (getGraphRef(resolvedId).kind !== GraphRefType.Param) {
        throw new Error();
      }

      return resolvedId;
    },
    [INode, entryId, getGraphRef, graphId, initGraph]
  );

  const resolveFieldGraph = useCallback((): GraphId => {
    const storedGraphId = IEntry(entryId, EntryType.Graph).getGraphId();
    let resolvedId = graphId ?? storedGraphId;
    if (!resolvedId) {
      resolvedId = initGraph({
        ref: {
          kind: GraphRefType.Field,
          fieldId: entryId,
        },
      });

      linkGraph(entryId, resolvedId);
    }

    // Sanity check
    if (getGraphRef(resolvedId).kind !== GraphRefType.Field) {
      throw new Error();
    }

    return resolvedId;
  }, [IEntry, entryId, getGraphRef, graphId, initGraph, linkGraph]);

  const resolveGraph = useCallback(
    (state?: GraphNavigationState): GraphId => {
      // When the navigation state is defined we are dealing with a parameter graph,
      // otherwise it is a graph linked directly to an entry.
      return match(state)
        .with(P.nonNullable, resolveParameterGraph)
        .with(P.nullish, resolveFieldGraph)
        .exhaustive();
    },
    [resolveParameterGraph, resolveFieldGraph]
  );

  return { resolveGraph };
};
